import { DocumentNode, gql, useMutation } from '@apollo/client';
import { Button, Text, useDisclosure, useToast } from '@chakra-ui/react';
import * as React from 'react';

import { EModItemResolutionState } from '@/client/legacy-shared-modules/moderation-item/moderationType';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { logError } from '@/imports/logging/ClientLogger';
import { AlertCircleIcon } from '@/imports/ui/chakra/feather';

import { PendingModerationState_PostFragment } from './graphql-types/PendingModerationState_PostFragment';
import {
  PendingModerationState_UpdateModerationItem,
  PendingModerationState_UpdateModerationItemVariables,
} from './graphql-types/PendingModerationState_UpdateModerationItem';

const POST_FRAGMENT = gql`
  fragment PendingModerationState_PostFragment on PostV2 {
    _id
    moderationItem {
      _id
    }
    currentUserCanResolveModeratedState
  }
`;

const MODERATION_ITEM_MUTATION = gql`
  mutation PendingModerationState_UpdateModerationItem(
    $input: UpdateModerationItemInputV2!
  ) {
    updateModerationItemV2(input: $input) {
      data {
        _id
        resolutionState
        postV2 {
          _id
          currentUserCanResolveModeratedState
          currentUserCanRespondToPost
          currentUserCanUpdatePost
          currentUserCanEditAnnouncedState
        }
      }
    }
  }
`;

export const PendingModerationState: React.FC<{
  post: PendingModerationState_PostFragment;
}> & {
  postFragment: DocumentNode;
} = ({ post }) => {
  const confirmDialog = useDisclosure();

  const [editModerationItem] = useMutation<
    PendingModerationState_UpdateModerationItem,
    PendingModerationState_UpdateModerationItemVariables
  >(MODERATION_ITEM_MUTATION);

  const toast = useToast();

  const approve = async (): Promise<void> => {
    if (post.moderationItem) {
      try {
        await editModerationItem({
          variables: {
            input: {
              id: post.moderationItem._id,
              data: {
                resolutionState: EModItemResolutionState.APPROVED,
              },
            },
          },
        });
        toast({ title: 'This post was approved', status: 'success' });
      } catch (e) {
        toast({ title: 'Failed to approve this post', status: 'error' });
        logError(e);
      }
    } else {
      toast({ title: 'Failed to approve this post', status: 'error' });
    }
  };

  const reject = async (): Promise<void> => {
    if (post.moderationItem) {
      try {
        await editModerationItem({
          variables: {
            input: {
              id: post.moderationItem._id,
              data: {
                resolutionState: EModItemResolutionState.REJECTED,
              },
            },
          },
        });
        toast({ title: 'This post was rejected', status: 'success' });
      } catch (e) {
        toast({ title: 'Failed to reject this post', status: 'error' });
        logError(e);
      }
    } else {
      toast({ title: 'Failed to reject this post', status: 'error' });
    }
  };

  return (
    <>
      <AlertCircleIcon color="yellow.500" />
      <Text textStyle="compact" as="span" flexGrow="1">
        This post is pending moderation
      </Text>
      {post.currentUserCanResolveModeratedState && (
        <>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={confirmDialog.onOpen}
          >
            Reject
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={approve}
          >
            Approve
          </Button>
          <ConfirmDialog
            isOpen={confirmDialog.isOpen}
            onClose={confirmDialog.onClose}
            title={'Are you sure you want to reject this post?'}
            body={
              'Rejecting this post will hide it from other community members. Are you sure you want to reject this post? '
            }
            confirmButtonText={'Reject Post'}
            onConfirm={reject}
          />
        </>
      )}
    </>
  );
};
PendingModerationState.postFragment = POST_FRAGMENT;
